import {
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

//
// When the callout beside a pinned group header opens, which header it follows, and when it closes.
// Independent of Fluent and of the model: the renderer marks the rows that have a hint, attaches
// `railHandlers` to the menu root and `calloutHandlers` to the callout, and reads `hint`.
//

export interface INavHintTarget {
  /** The entry the hint is about. */
  id: string;
  /** The row element the callout is anchored to. */
  element: HTMLElement;
}

export interface INavHintOpenOptions {
  /** Move keyboard focus into the callout once it is open. */
  focus?: boolean;
}

export interface INavHintHover {
  /** The row an open hint belongs to; undefined while none is open. */
  hint: INavHintTarget | undefined;
  /** True when the open hint was asked to take keyboard focus. */
  focusRequested: boolean;
  /** Opens for a row at once, or moves to it. */
  open: (row: INavHintTarget, options?: INavHintOpenOptions) => void;
  close: () => void;
  railHandlers: {
    onMouseOver: (ev: ReactMouseEvent<HTMLElement>) => void;
    onMouseMove: (ev: ReactMouseEvent<HTMLElement>) => void;
    onMouseLeave: () => void;
    onFocus: (ev: ReactFocusEvent<HTMLElement>) => void;
    onKeyDown: (ev: ReactKeyboardEvent<HTMLElement>) => void;
  };
  calloutHandlers: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
}

// A pointer passing the rail must not open a hint for every row it crosses.
const navHintOpenDelay = 100;
// Long enough for the pointer to cross the gap between the rail and the callout.
const navHintCloseDelay = 200;
// A pointer heading for the callout crosses other rows on the way. The change those rows ask for
// waits this long, and every further move towards the callout starts the wait again.
const navHintIntentDelay = 300;

// The renderer puts the entry's id on every row that has a hint (navLinks.ts).
const navHintRowSelector = '[data-nav-id]';
// Any row, hint or not.
const navRowSelector = '.ms-Nav-compositeLink';

/** The hint row that contains the target, if any. */
export const findNavHintRow = (target: EventTarget | null): INavHintTarget | undefined => {
  const element = target instanceof Element ? target.closest<HTMLElement>(navHintRowSelector) : null;
  const id = element?.getAttribute('data-nav-id');
  if (!element || !id) {
    return undefined;
  }

  return { id, element };
};

const isInsideRow = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(navRowSelector) !== null;

const clearTimer = (timer: MutableRefObject<number | undefined>): void => {
  if (timer.current !== undefined) {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  }
};

export const useNavHintHover = (): INavHintHover => {
  const [hint, setHint] = useState<INavHintTarget | undefined>(undefined);
  const [focusRequested, setFocusRequested] = useState<boolean>(false);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);
  const intentTimer = useRef<number | undefined>(undefined);
  // The row the open timer will open, and the change the intent timer will apply.
  const pendingRow = useRef<INavHintTarget | undefined>(undefined);
  const pendingChange = useRef<(() => void) | undefined>(undefined);
  // The callout opens to the right of the menu. A pointer further right than where it last was on
  // the hint's row is heading for the callout.
  const rowExitX = useRef<number>(0);
  const lastPointerX = useRef<number>(0);

  const clearAllTimers = useCallback(() => {
    clearTimer(openTimer);
    clearTimer(closeTimer);
    clearTimer(intentTimer);
    pendingChange.current = undefined;
  }, []);

  const close = useCallback(() => {
    clearAllTimers();
    setHint(undefined);
    setFocusRequested(false);
  }, [clearAllTimers]);

  const open = useCallback(
    (row: INavHintTarget, options?: INavHintOpenOptions) => {
      clearAllTimers();
      setHint((current) => (current?.id === row.id && current.element === row.element ? current : row));
      setFocusRequested(options?.focus === true);
    },
    [clearAllTimers],
  );

  const scheduleClose = useCallback(() => {
    clearAllTimers();
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = undefined;
      setHint(undefined);
      setFocusRequested(false);
    }, navHintCloseDelay);
  }, [clearAllTimers]);

  const armIntent = useCallback(() => {
    clearTimer(intentTimer);
    intentTimer.current = window.setTimeout(() => {
      intentTimer.current = undefined;
      const change = pendingChange.current;
      pendingChange.current = undefined;
      change?.();
    }, navHintIntentDelay);
  }, []);

  // A move onto another row while a callout is open: applied at once when the pointer moves away
  // from the callout, deferred while it moves towards it.
  const requestChange = useCallback(
    (change: () => void, pointerX: number) => {
      if (pointerX <= rowExitX.current) {
        clearTimer(intentTimer);
        pendingChange.current = undefined;
        change();

        return;
      }

      pendingChange.current = change;
      armIntent();
    },
    [armIntent],
  );

  // A timer must not fire into a menu that is gone.
  useEffect(() => () => clearAllTimers(), [clearAllTimers]);

  // The row an open hint belongs to can disappear when the model changes.
  useEffect(() => {
    if (hint && !hint.element.isConnected) {
      close();
    }
  });

  const onMouseOver = useCallback(
    (ev: ReactMouseEvent<HTMLElement>) => {
      clearTimer(closeTimer);

      const row = findNavHintRow(ev.target);
      if (!row) {
        clearTimer(openTimer);
        // On a row without a hint the open one is over. Over a caption or the padding it stays.
        if (hint && isInsideRow(ev.target)) {
          requestChange(close, ev.clientX);
        }

        return;
      }

      if (hint) {
        if (hint.id === row.id) {
          // Back on the hint's own row: whatever another row asked for is off.
          clearTimer(intentTimer);
          pendingChange.current = undefined;

          return;
        }

        requestChange(() => setHint(row), ev.clientX);

        return;
      }

      pendingRow.current = row;
      if (openTimer.current === undefined) {
        openTimer.current = window.setTimeout(() => {
          openTimer.current = undefined;
          setHint(pendingRow.current);
          setFocusRequested(false);
        }, navHintOpenDelay);
      }
    },
    [close, hint, requestChange],
  );

  const onMouseMove = useCallback(
    (ev: ReactMouseEvent<HTMLElement>) => {
      if (hint && findNavHintRow(ev.target)?.id === hint.id) {
        rowExitX.current = ev.clientX;
      }

      // Still moving towards the callout: the pending change waits longer.
      if (intentTimer.current !== undefined && ev.clientX > lastPointerX.current) {
        armIntent();
      }

      lastPointerX.current = ev.clientX;
    },
    [armIntent, hint],
  );

  const onMouseLeave = useCallback(() => {
    clearTimer(openTimer);
    clearTimer(intentTimer);
    pendingChange.current = undefined;
    if (hint) {
      scheduleClose();
    }
  }, [hint, scheduleClose]);

  // The keyboard gets the hint at once. Focus on a row without a hint ends the open one.
  const onFocus = useCallback(
    (ev: ReactFocusEvent<HTMLElement>) => {
      const row = findNavHintRow(ev.target);
      if (!row) {
        close();

        return;
      }

      open(row);
    },
    [close, open],
  );

  const onKeyDown = useCallback(
    (ev: ReactKeyboardEvent<HTMLElement>) => {
      if (hint && ev.key === 'Escape') {
        // The flyout's panel listens for Escape too; it would close and reopen.
        ev.stopPropagation();
        close();
      }
    },
    [close, hint],
  );

  const onCalloutMouseEnter = useCallback(() => {
    clearTimer(closeTimer);
    clearTimer(intentTimer);
    pendingChange.current = undefined;
  }, []);

  const onCalloutMouseLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  return {
    hint,
    focusRequested,
    open,
    close,
    railHandlers: { onMouseOver, onMouseMove, onMouseLeave, onFocus, onKeyDown },
    calloutHandlers: { onMouseEnter: onCalloutMouseEnter, onMouseLeave: onCalloutMouseLeave },
  };
};
